import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Wrench, CheckCircle2, Shield, Clock, Sparkles } from "lucide-react";
import dishwasherImage from "@/assets/dishwasher-installation-small.webp";

const ApplianceInstallationSection = () => {
  const { t, language } = useLanguage();

  const features = [
    t("appliance.feature1"),
    t("appliance.feature2"),
    t("appliance.feature3"),
    t("appliance.feature4"),
    t("appliance.feature5"),
    t("appliance.feature6"),
  ];

  const getWhatsAppLink = () => {
    const message = t("appliance.whatsapp.message1");
    return `https://wa.me/358402401909?text=${encodeURIComponent(message)}`;
  };

  const icons = [
    { Icon: Wrench, color: "text-blue-400" },
    { Icon: CheckCircle2, color: "text-green-400" },
    { Icon: Shield, color: "text-purple-400" },
    { Icon: Clock, color: "text-orange-400" },
  ];

  return (
    <section className="relative py-20 overflow-hidden appliance-installation-section">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-blue-900"></div>
      
      {/* Floating Icons Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {icons.map((item, index) => (
          <div
            key={index}
            className={`absolute appliance-float opacity-10 ${item.color}`}
            style={{
              left: `${20 + index * 20}%`,
              top: `${10 + index * 15}%`,
              animationDelay: `${index * 0.5}s`,
            }}
          >
            <item.Icon size={60} />
          </div>
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Subtitle */}
          <div className="text-center mb-4">
            <span className="inline-block px-6 py-2 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-sm font-semibold tracking-wide uppercase">
              {t("appliance.subtitle")}
            </span>
          </div>

          {/* Animated Title */}
          <h2 className="text-4xl md:text-6xl font-bold text-center mb-6 appliance-title">
            {t("appliance.title")}
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Image and Price */}
            <div className="relative">
              {/* Hero Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src={dishwasherImage} 
                  alt={language === "en" ? "Professional dishwasher and washing machine installation" : "Ammattimainen astianpesukoneen ja pesukoneen asennus"}
                  className="aspect-[4/3] object-cover w-full group-hover:scale-105 transition-smooth"
                />
                {/* Animated border */}
                <div className="absolute inset-0 border-4 border-transparent group-hover:border-blue-400 transition-smooth rounded-2xl"></div>
              </div>

              {/* Floating Price Badge */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-4 rounded-2xl shadow-xl appliance-pulse">
                <Sparkles className="inline-block mr-2 mb-1" size={20} />
                <span className="text-3xl font-bold">{t("appliance.price")}</span>
              </div>
            </div>

            {/* Right Column - Content */}
            <div>
              {/* Description */}
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t("appliance.description")}
              </p>

              {/* Features List */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-foreground flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={24} />
                  {t("appliance.features.title")}
                </h3>
                <div className="grid gap-3">
                  {features.map((feature, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-slate-800 transition-smooth appliance-feature"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <Button
                size="lg"
                className="w-full bg-green-500 hover:bg-green-600 text-white text-lg py-6 rounded-xl shadow-xl hover:shadow-2xl transition-smooth hover:scale-105 appliance-cta"
                onClick={() => window.open(getWhatsAppLink(), "_blank")}
              >
                <svg className="mr-2" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                {t("appliance.cta")}
              </Button>

              {/* Trust Indicators */}
              <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Shield size={16} className="text-blue-500" />
                  <span>{language === "en" ? "Certified" : "Sertifioitu"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={16} className="text-green-500" />
                  <span>{language === "en" ? "Same-day" : "Saman päivän"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle2 size={16} className="text-purple-500" />
                  <span>{language === "en" ? "Warranty" : "Takuu"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplianceInstallationSection;
